import type { Client, Project, Employee, Timesheet, TimesheetEntry, CostCentre, ExtendedWorkingPattern, WorkingDay } from '@/types/timesheet';
import { addDays, startOfWeek, subWeeks } from 'date-fns';
import { mockScheduledBookings } from './mockScheduledBookings';
import { mockProjects as projectsFromProjectsData } from './projectsData';

// Helper function to create working pattern with auto-calculated daily hours
function createWorkingPattern(
  hoursPerWeek: number,
  workingDays: { monday: boolean; tuesday: boolean; wednesday: boolean; thursday: boolean; friday: boolean; saturday: boolean; sunday: boolean },
  notes?: string,
  customHours?: Partial<Record<keyof typeof workingDays, number>>
): ExtendedWorkingPattern {
  const dayNames: (keyof typeof workingDays)[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const numWorkingDays = dayNames.filter(day => workingDays[day]).length;
  const defaultHoursPerDay = numWorkingDays > 0 ? hoursPerWeek / numWorkingDays : 0;

  const pattern: ExtendedWorkingPattern = {
    hoursPerWeek: hoursPerWeek,
    standardHoursPerWeek: hoursPerWeek,
    workingDays: {} as any,
    notes
  };

  dayNames.forEach(day => {
    const isWorking = workingDays[day];
    const hasCustomHours = customHours && customHours[day] !== undefined;
    pattern.workingDays[day] = {
      isWorkingDay: isWorking,
      expectedHours: hasCustomHours ? customHours[day]! : (isWorking ? defaultHoursPerDay : 0),
      isCustom: hasCustomHours
    };
  });

  return pattern;
}

// Realistic client names for IT services/consulting
const clientNames = [
  // Enterprise
  'Barclays Bank', 'HSBC Holdings', 'Lloyds Banking Group', 'NatWest Group',
  'Aviva Insurance', 'Legal & General', 'Prudential Financial',
  'Tesco PLC', 'Sainsbury\'s', 'Marks & Spencer',
  'BT Group', 'Vodafone UK', 'Sky Broadcasting',
  'BP Energy', 'Shell UK', 'National Grid',
  // Mid-market
  'Octopus Energy', 'Monzo Bank', 'Revolut', 'Starling Bank',
  'Deliveroo', 'Just Eat', 'Gousto', 'HelloFresh UK',
  'Cazoo', 'Auto Trader', 'Rightmove', 'Zoopla',
  'Trainline', 'Skyscanner', 'Booking.com UK',
  // Public Sector
  'NHS Digital', 'HMRC', 'Department for Work and Pensions',
  'Ministry of Defence', 'Home Office', 'Department for Transport',
  'Greater London Authority', 'Transport for London',
  'Manchester City Council', 'Birmingham City Council'
];

const projectTypes = [
  'Cloud Migration', 'Digital Transformation', 'System Implementation',
  'Application Development', 'Infrastructure Upgrade', 'Security Assessment',
  'Data Analytics Platform', 'API Integration', 'Managed Services',
  'DevOps Transformation', 'Cyber Security', 'Business Intelligence',
  'Mobile App Development', 'Microservices Architecture', 'Legacy Modernisation'
];

const activityTypes = [
  'Client Meeting', 'Workshop', 'Stand-up', 'Stakeholder Meeting',
  'Development', 'Configuration', 'Build', 'Testing',
  'Documentation', 'Design Document', 'Runbook', 'User Guide',
  'Sprint Planning', 'Sprint Review', 'Team Meeting', 'Retrospective',
  'Research', 'Proof of Concept', 'Spike Work',
  'Travel', 'Client Site Visit'
];

const costCentres: CostCentre[] = [
  { id: 'cc-dev', code: 'DEV', name: 'Development' },
  { id: 'cc-test', code: 'TEST', name: 'Testing' },
  { id: 'cc-pm', code: 'PM', name: 'Project Management' },
  { id: 'cc-arch', code: 'ARCH', name: 'Architecture' },
  { id: 'cc-support', code: 'SUP', name: 'Support' },
  { id: 'cc-training', code: 'TRN', name: 'Training' },
  { id: 'cc-sales', code: 'SALES', name: 'Pre-Sales' },
  { id: 'cc-r&d', code: 'R&D', name: 'Research & Development' },
];

// Generate clients
export const generateClients = (): Client[] => {
  return clientNames.map((name, index) => ({
    id: `client-${index + 1}`,
    name,
    sector: index < 17 ? 'enterprise' : index < 31 ? 'mid-market' : 'public-sector'
  }));
};

// Generate 200+ projects
export const generateProjects = (clients: Client[]): Project[] => {
  const projects: Project[] = [];
  let projectId = 1;

  clients.forEach(client => {
    // Each client has 3-12 projects
    const projectCount = Math.floor(Math.random() * 10) + 3;
    
    for (let i = 0; i < projectCount; i++) {
      const projectType = projectTypes[Math.floor(Math.random() * projectTypes.length)];
      const code = `${client.name.substring(0, 3).toUpperCase()}-${String(projectId).padStart(4, '0')}`;
      
      // Determine team size based on project type
      let teamSize = 5;
      if (projectType.includes('Migration') || projectType.includes('Transformation')) {
        teamSize = Math.floor(Math.random() * 11) + 15; // 15-25
      } else if (projectType.includes('Development') || projectType.includes('Implementation')) {
        teamSize = Math.floor(Math.random() * 8) + 5; // 5-12
      } else {
        teamSize = Math.floor(Math.random() * 3) + 2; // 2-4
      }

      // 85% billable, 15% non-billable
      const billable = Math.random() > 0.15;

      projects.push({
        id: `project-${projectId}`,
        code,
        name: projectType,
        clientId: client.id,
        clientName: client.name,
        costCentres: costCentres.slice(0, Math.floor(Math.random() * 4) + 2),
        billable,
        active: true,
        teamSize
      });

      projectId++;
    }
  });

  return projects;
};

// Generate employees - using database seed data with varied working patterns
export const generateEmployees = (): Employee[] => {
  const employees: Employee[] = [
    // Standard full-time (40h, Mon-Fri) - Most common
    { id: 1, name: 'James Smith', email: 'james.smith@timetrackpro.com', department: 'Delivery', role: 'Senior Consultant', managerId: undefined, photoUrl: 'https://picsum.photos/seed/James-Smith-1/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Part-time (20h, Mon-Wed) - 3 days
    { id: 2, name: 'Sarah Jones', email: 'sarah.jones@timetrackpro.com', department: 'Cloud Services', role: 'Architect', managerId: undefined, photoUrl: 'https://picsum.photos/seed/Sarah-Jones-2/300/300', workingPattern: createWorkingPattern(20, { monday: true, tuesday: true, wednesday: true, thursday: false, friday: false, saturday: false, sunday: false }) },
    
    // 4-day week (32h, Mon-Thu)
    { id: 3, name: 'Michael Williams', email: 'michael.williams@timetrackpro.com', department: 'Application Development', role: 'Technical Lead', managerId: undefined, photoUrl: 'https://picsum.photos/seed/Michael-Williams-3/300/300', workingPattern: createWorkingPattern(32, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: false, saturday: false, sunday: false }) },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 4, name: 'Emma Brown', email: 'emma.brown@timetrackpro.com', department: 'Infrastructure', role: 'Project Manager', managerId: undefined, photoUrl: 'https://picsum.photos/seed/Emma-Brown-4/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Compressed week (40h, 4x10 Mon-Thu) - Custom hours per day
    { id: 5, name: 'David Taylor', email: 'david.taylor@timetrackpro.com', department: 'Security', role: 'Senior Consultant', managerId: undefined, photoUrl: 'https://picsum.photos/seed/David-Taylor-5/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: false, saturday: false, sunday: false }, '4x10 compressed schedule', { monday: 10, tuesday: 10, wednesday: 10, thursday: 10 }) },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 6, name: 'Sophie Davies', email: 'sophie.davies@timetrackpro.com', department: 'Delivery', role: 'Test Lead', managerId: undefined, photoUrl: 'https://picsum.photos/seed/Sophie-Davies-6/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Weekend worker (40h, Tue-Sat) - Retail/Support
    { id: 7, name: 'Robert Wilson', email: 'robert.wilson@timetrackpro.com', department: 'Cloud Services', role: 'DevOps Engineer', managerId: undefined, photoUrl: 'https://picsum.photos/seed/Robert-Wilson-7/300/300', workingPattern: createWorkingPattern(40, { monday: false, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: true, sunday: false }, 'Weekend support rotation') },
    
    // Part-time (24h, Mon-Wed)
    { id: 8, name: 'Emily Evans', email: 'emily.evans@timetrackpro.com', department: 'Application Development', role: 'Business Analyst', managerId: undefined, photoUrl: 'https://picsum.photos/seed/Emily-Evans-8/300/300', workingPattern: createWorkingPattern(24, { monday: true, tuesday: true, wednesday: true, thursday: false, friday: false, saturday: false, sunday: false }) },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 9, name: 'John Thomas', email: 'john.thomas@timetrackpro.com', department: 'Infrastructure', role: 'Architect', managerId: undefined, photoUrl: 'https://picsum.photos/seed/John-Thomas-9/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Extended hours (45h, Mon-Fri) - Senior role
    { id: 10, name: 'Jessica Roberts', email: 'jessica.roberts@timetrackpro.com', department: 'Security', role: 'Senior Consultant', managerId: undefined, photoUrl: 'https://picsum.photos/seed/Jessica-Roberts-10/300/300', workingPattern: createWorkingPattern(45, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }, 'Senior role with extended hours') },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 11, name: 'William Johnson', email: 'william.johnson@timetrackpro.com', department: 'Delivery', role: 'Senior Developer', managerId: 1, photoUrl: 'https://picsum.photos/seed/William-Johnson-11/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Part-time (16h, Mon-Tue) - 2 days
    { id: 12, name: 'Hannah Walker', email: 'hannah.walker@timetrackpro.com', department: 'Cloud Services', role: 'Developer', managerId: 2, photoUrl: 'https://picsum.photos/seed/Hannah-Walker-12/300/300', workingPattern: createWorkingPattern(16, { monday: true, tuesday: true, wednesday: false, thursday: false, friday: false, saturday: false, sunday: false }) },
    
    // 4-day week (32h, Mon-Thu)
    { id: 13, name: 'Thomas Wright', email: 'thomas.wright@timetrackpro.com', department: 'Application Development', role: 'Consultant', managerId: 3, photoUrl: 'https://picsum.photos/seed/Thomas-Wright-13/300/300', workingPattern: createWorkingPattern(32, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: false, saturday: false, sunday: false }) },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 14, name: 'Lucy Robinson', email: 'lucy.robinson@timetrackpro.com', department: 'Infrastructure', role: 'Developer', managerId: 4, photoUrl: 'https://picsum.photos/seed/Lucy-Robinson-14/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 15, name: 'Daniel Thompson', email: 'daniel.thompson@timetrackpro.com', department: 'Security', role: 'Consultant', managerId: 5, photoUrl: 'https://picsum.photos/seed/Daniel-Thompson-15/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Part-time (28h, Mon-Thu) - 4 days reduced hours
    { id: 16, name: 'Olivia White', email: 'olivia.white@timetrackpro.com', department: 'Delivery', role: 'Developer', managerId: 1, photoUrl: 'https://picsum.photos/seed/Olivia-White-16/300/300', workingPattern: createWorkingPattern(28, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: false, saturday: false, sunday: false }) },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 17, name: 'Matthew Hughes', email: 'matthew.hughes@timetrackpro.com', department: 'Cloud Services', role: 'Senior Developer', managerId: 2, photoUrl: 'https://picsum.photos/seed/Matthew-Hughes-17/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 18, name: 'Charlotte Edwards', email: 'charlotte.edwards@timetrackpro.com', department: 'Application Development', role: 'Developer', managerId: 3, photoUrl: 'https://picsum.photos/seed/Charlotte-Edwards-18/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // UK Standard (37.5h, Mon-Fri)
    { id: 19, name: 'Andrew Green', email: 'andrew.green@timetrackpro.com', department: 'Infrastructure', role: 'Consultant', managerId: 4, photoUrl: 'https://picsum.photos/seed/Andrew-Green-19/300/300', workingPattern: createWorkingPattern(37.5, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 20, name: 'Amelia Hall', email: 'amelia.hall@timetrackpro.com', department: 'Security', role: 'Developer', managerId: 5, photoUrl: 'https://picsum.photos/seed/Amelia-Hall-20/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 21, name: 'Christopher Johnson', email: 'christopher.johnson@timetrackpro.com', department: 'Delivery', role: 'Consultant', managerId: 6, photoUrl: 'https://picsum.photos/seed/Christopher-Johnson-21/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Part-time (30h, Mon-Fri) - Reduced daily hours (6h/day)
    { id: 22, name: 'Grace Walker', email: 'grace.walker@timetrackpro.com', department: 'Cloud Services', role: 'Developer', managerId: 7, photoUrl: 'https://picsum.photos/seed/Grace-Walker-22/300/300', workingPattern: createWorkingPattern(30, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }, '6 hours per day') },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 23, name: 'Joshua Wright', email: 'joshua.wright@timetrackpro.com', department: 'Application Development', role: 'Senior Developer', managerId: 8, photoUrl: 'https://picsum.photos/seed/Joshua-Wright-23/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // 4-day week (36h, Mon-Thu) - 9 hour days
    { id: 24, name: 'Lily Robinson', email: 'lily.robinson@timetrackpro.com', department: 'Infrastructure', role: 'Developer', managerId: 9, photoUrl: 'https://picsum.photos/seed/Lily-Robinson-24/300/300', workingPattern: createWorkingPattern(36, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: false, saturday: false, sunday: false }) },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 25, name: 'Ryan Thompson', email: 'ryan.thompson@timetrackpro.com', department: 'Security', role: 'Consultant', managerId: 10, photoUrl: 'https://picsum.photos/seed/Ryan-Thompson-25/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 26, name: 'Megan White', email: 'megan.white@timetrackpro.com', department: 'Delivery', role: 'Developer', managerId: 1, photoUrl: 'https://picsum.photos/seed/Megan-White-26/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Part-time (20h, Thu-Fri) - End of week only
    { id: 27, name: 'Benjamin Hughes', email: 'benjamin.hughes@timetrackpro.com', department: 'Cloud Services', role: 'Consultant', managerId: 2, photoUrl: 'https://picsum.photos/seed/Benjamin-Hughes-27/300/300', workingPattern: createWorkingPattern(20, { monday: false, tuesday: false, wednesday: false, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 28, name: 'Chloe Edwards', email: 'chloe.edwards@timetrackpro.com', department: 'Application Development', role: 'Developer', managerId: 3, photoUrl: 'https://picsum.photos/seed/Chloe-Edwards-28/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 29, name: 'Samuel Green', email: 'samuel.green@timetrackpro.com', department: 'Infrastructure', role: 'Senior Developer', managerId: 4, photoUrl: 'https://picsum.photos/seed/Samuel-Green-29/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
    
    // Standard full-time (40h, Mon-Fri)
    { id: 30, name: 'Isabella Hall', email: 'isabella.hall@timetrackpro.com', department: 'Security', role: 'Developer', managerId: 5, photoUrl: 'https://picsum.photos/seed/Isabella-Hall-30/300/300', workingPattern: createWorkingPattern(40, { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }) },
  ];

  return employees;
};

// Generate realistic timesheet entries
const generateRealisticEntries = (projects: Project[], weekStart: Date): TimesheetEntry[] => {
  const entries: TimesheetEntry[] = [];
  const numProjects = Math.floor(Math.random() * 4) + 2; // 2-5 projects per week
  const selectedProjects = projects
    .sort(() => Math.random() - 0.5)
    .slice(0, numProjects);

  selectedProjects.forEach((project, index) => {
    const costCentre = project.costCentres[Math.floor(Math.random() * project.costCentres.length)];
    const activity = activityTypes[Math.floor(Math.random() * activityTypes.length)];
    
    // Distribute 37.5-45 hours across projects
    const baseHours = index === 0 ? 20 : 10;
    const variance = Math.random() * 10 - 5;
    let totalHours = Math.max(5, baseHours + variance);

    // Distribute across weekdays (mostly)
    const hours = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0
    };

    // Typical pattern: 7.5 hours per day
    const daysToWork = Math.floor(totalHours / 7.5);
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;
    
    for (let i = 0; i < daysToWork && i < 5; i++) {
      hours[days[i]] = 7.5;
      totalHours -= 7.5;
    }

    // Distribute remaining hours
    if (totalHours > 0) {
      const day = days[Math.floor(Math.random() * 5)];
      hours[day] += totalHours;
    }

    // 5% chance of weekend work (go-lives, deployments)
    if (Math.random() < 0.05) {
      hours.saturday = Math.floor(Math.random() * 4) + 2;
    }

    // 2% chance of excessive hours (critical incidents)
    if (Math.random() < 0.02) {
      const day = days[Math.floor(Math.random() * 5)];
      hours[day] = Math.floor(Math.random() * 4) + 11; // 11-14 hours
    }

    entries.push({
      id: -(entries.length + 1),
      projectId: project.id,
      costCentreId: costCentre.id,
      description: activity,
      hours
    });
  });

  return entries;
};

// Generate timesheets for multiple weeks
export const generateTimesheets = (employees: Employee[], projects: Project[]): Timesheet[] => {
  const timesheets: Timesheet[] = [];
  const currentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
  
  // Employees with scheduled bookings that we want to ensure are visible in approval queue
  const employeesWithScheduledBookings = [11, 12, 13];

  // Generate for current week and previous 4 weeks
  for (let weekOffset = 0; weekOffset < 5; weekOffset++) {
    const weekStart = subWeeks(currentWeek, weekOffset);
    const weekEnding = addDays(weekStart, 6);

    employees.forEach(employee => {
      let status: 'draft' | 'submitted' | 'approved' | 'locked' = 'draft';
      let approverId: number | undefined = undefined;
      let submittedAt: Date | undefined = undefined;
      let approvedAt: Date | undefined = undefined;
      
      // 🎯 GUARANTEE: Employees with scheduled bookings for current week are ALWAYS submitted
      const hasScheduledBookings = employeesWithScheduledBookings.includes(employee.id);
      const isCurrentWeek = weekOffset === 0;
      
      if (isCurrentWeek && hasScheduledBookings) {
        // Force these to be submitted so they appear in approval queue
        status = 'submitted';
        approverId = employee.managerId;
        submittedAt = addDays(weekEnding, -2);
      } else if (weekOffset === 0) {
        // Current week: 40% submitted (pending approval), 60% drafts
        // Force employee ID 1 (James Smith) to be DRAFT for testing
        if (employee.id === 1) {
          status = 'draft';
        } else if (Math.random() > 0.6) {
          status = 'submitted';
          approverId = employee.managerId;
          submittedAt = addDays(weekEnding, -2); // Submitted 2 days before week end
        } else {
          status = 'draft';
        }
      } else if (weekOffset === 1) {
        // Last week: 60% submitted (pending approval), 30% approved, 10% draft
        const rand = Math.random();
        if (rand > 0.4) {
          status = 'submitted';
          approverId = employee.managerId;
          submittedAt = addDays(weekEnding, 1); // Submitted day after week end
        } else if (rand > 0.1) {
          status = 'approved';
          approverId = employee.managerId;
          submittedAt = addDays(weekEnding, 1);
          approvedAt = addDays(weekEnding, 2);
        } else {
          status = 'draft';
        }
      } else if (weekOffset === 2) {
        // 2 weeks ago: 30% submitted (overdue!), 60% approved, 10% locked
        const rand = Math.random();
        if (rand > 0.7) {
          status = 'submitted';
          approverId = employee.managerId;
          submittedAt = addDays(weekEnding, 1);
        } else if (rand > 0.1) {
          status = 'approved';
          approverId = employee.managerId;
          submittedAt = addDays(weekEnding, 1);
          approvedAt = addDays(weekEnding, 3);
        } else {
          status = 'locked';
          approverId = employee.managerId;
          submittedAt = addDays(weekEnding, 1);
          approvedAt = addDays(weekEnding, 2);
        }
      } else {
        // Older weeks: mostly locked (in payroll), some approved
        status = Math.random() > 0.2 ? 'locked' : 'approved';
        approverId = employee.managerId;
        submittedAt = addDays(weekEnding, 1);
        approvedAt = addDays(weekEnding, 2);
      }

      const entries = generateRealisticEntries(projects, weekStart);

      timesheets.push({
        id: employee.id * 1000 + weekOffset,
        employeeId: employee.id,
        employeeName: employee.name,
        weekEnding,
        status,
        entries,
        submittedAt,
        approvedAt,
        approverId
      });
    });
  }

  return timesheets;
};

// Add scheduled booking data to timesheets
const enhanceTimesheetsWithScheduledBookings = (timesheets: Timesheet[], projects: Project[]): Timesheet[] => {
  console.log('='.repeat(80));
  console.log('🔧 ENHANCEMENT FUNCTION CALLED - Enhancing timesheets with scheduled bookings...');
  console.log(`📊 Total timesheets: ${timesheets.length}`);
  console.log(`📅 Total scheduled bookings: ${mockScheduledBookings.length}`);
  console.log('='.repeat(80));
  
  // Store in window for debugging
  (window as any).__ENHANCEMENT_CALLED__ = true;
  (window as any).__TOTAL_TIMESHEETS__ = timesheets.length;
  (window as any).__TOTAL_BOOKINGS__ = mockScheduledBookings.length;
  
  let enhancedCount = 0;
  
  return timesheets.map(timesheet => {
    // Only enhance submitted timesheets for current and last week
    if (timesheet.status !== 'submitted') {
      return timesheet;
    }

    // Find scheduled bookings for this employee and week
    const bookingsForWeek = mockScheduledBookings.filter(
      booking =>
        booking.employeeId === timesheet.employeeId &&
        booking.weekEnding.getTime() === timesheet.weekEnding.getTime()
    );

    if (bookingsForWeek.length === 0) {
      return timesheet;
    }
    
    enhancedCount++;
    console.log(`✅ Enhancing timesheet for ${timesheet.employeeName} (${bookingsForWeek.length} bookings)`);
    console.log(`   Week ending: ${timesheet.weekEnding.toISOString()}`);
    console.log(`   Bookings:`, bookingsForWeek.map(b => `${b.projectName} (${b.costCentreName})`));

    // Create a map of existing entries by project+costCentre
    const existingEntriesMap = new Map<string, TimesheetEntry>();
    timesheet.entries.forEach(entry => {
      const key = `${entry.projectId}-${entry.costCentreId}`;
      existingEntriesMap.set(key, entry);
    });

    // Process each booking
    const enhancedEntries: TimesheetEntry[] = [];
    const processedKeys = new Set<string>();

    bookingsForWeek.forEach((booking, index) => {
      const key = `${booking.projectId}-${booking.costCentreId}`;
      processedKeys.add(key);

      const existingEntry = existingEntriesMap.get(key);
      const project = projects.find(p => p.id === booking.projectId);

      if (existingEntry) {
        // Enhance existing entry with scheduled booking data
        // Simulate different variance scenarios
        let actualHours = { ...existingEntry.hours };
        let isDismissed = false;
        let dismissalReason = '';
        let dismissedAt: Date | undefined = undefined;

        // 🎯 GUARANTEE: First booking always has visible variance (>10%)
        // This ensures we always have at least one variance row visible in approval screen
        const isFirstBooking = index === 0;
        const shouldHaveVariance = isFirstBooking || Math.random() < 0.3;

        if (shouldHaveVariance && !isFirstBooking) {
          // Random variance for non-first bookings (30% chance)
          const varianceType = Math.random();
          if (varianceType < 0.5) {
            // Over schedule (logged more hours)
            actualHours = {
              monday: booking.scheduledHours.monday > 0 ? booking.scheduledHours.monday + 2 : 0,
              tuesday: booking.scheduledHours.tuesday > 0 ? booking.scheduledHours.tuesday + 2 : 0,
              wednesday: booking.scheduledHours.wednesday > 0 ? booking.scheduledHours.wednesday + 2 : 0,
              thursday: booking.scheduledHours.thursday > 0 ? booking.scheduledHours.thursday + 1 : 0,
              friday: booking.scheduledHours.friday > 0 ? booking.scheduledHours.friday + 1 : 0,
              saturday: 0,
              sunday: 0,
            };
          } else {
            // Under schedule (logged fewer hours)
            actualHours = {
              monday: booking.scheduledHours.monday > 0 ? Math.max(0, booking.scheduledHours.monday - 2) : 0,
              tuesday: booking.scheduledHours.tuesday > 0 ? Math.max(0, booking.scheduledHours.tuesday - 1) : 0,
              wednesday: booking.scheduledHours.wednesday > 0 ? Math.max(0, booking.scheduledHours.wednesday - 1) : 0,
              thursday: booking.scheduledHours.thursday > 0 ? booking.scheduledHours.thursday : 0,
              friday: booking.scheduledHours.friday > 0 ? booking.scheduledHours.friday : 0,
              saturday: 0,
              sunday: 0,
            };
          }
        } else if (isFirstBooking) {
          // First booking: ALWAYS create medium variance (13-15% over schedule)
          // This guarantees visibility in approval screen
          actualHours = {
            monday: booking.scheduledHours.monday > 0 ? booking.scheduledHours.monday + 1 : 0,
            tuesday: booking.scheduledHours.tuesday > 0 ? booking.scheduledHours.tuesday + 1 : 0,
            wednesday: booking.scheduledHours.wednesday > 0 ? booking.scheduledHours.wednesday + 1 : 0,
            thursday: booking.scheduledHours.thursday > 0 ? booking.scheduledHours.thursday + 1 : 0,
            friday: booking.scheduledHours.friday > 0 ? booking.scheduledHours.friday : 0,
            saturday: 0,
            sunday: 0,
          };
          console.log(`   🎯 GUARANTEED VARIANCE for ${booking.projectName}: Scheduled ${Object.values(booking.scheduledHours).reduce((a, b) => a + b, 0)}h → Logged ${Object.values(actualHours).reduce((a, b) => a + b, 0)}h`);
        } else if (Math.random() < 0.15) {
          // 15% chance of dismissal
          isDismissed = true;
          dismissalReason = [
            "Didn't work on this project this week",
            "Project was postponed",
            "Worked on different priorities",
            "Client meeting cancelled",
            "Reassigned to urgent task"
          ][Math.floor(Math.random() * 5)];
          dismissedAt = addDays(timesheet.weekEnding, -3);
          actualHours = {
            monday: 0,
            tuesday: 0,
            wednesday: 0,
            thursday: 0,
            friday: 0,
            saturday: 0,
            sunday: 0,
          };
        } else {
          // Match scheduled hours exactly
          actualHours = { ...booking.scheduledHours };
        }

        enhancedEntries.push({
          ...existingEntry,
          hours: actualHours,
          isFromSchedule: true,
          scheduledBookingId: booking.id,
          scheduledHours: booking.scheduledHours,
          bookingCreatedBy: booking.createdBy,
          bookingCreatedByName: booking.createdByName,
          bookingNotes: booking.notes,
          bookingCreatedAt: booking.createdAt,
          isDismissed,
          dismissalReason: isDismissed ? dismissalReason : undefined,
          dismissedAt: isDismissed ? dismissedAt : undefined,
          description: isDismissed ? '' : (existingEntry.description || 'Work as scheduled'),
        });
      } else {
        // Create new entry from booking
        const isDismissed = Math.random() < 0.1; // 10% chance of dismissal for new bookings
        const dismissalReason = isDismissed ? "Didn't work on this project" : '';

        enhancedEntries.push({
          id: -Date.now() - enhancedEntries.length,
          projectId: booking.projectId,
          costCentreId: booking.costCentreId,
          hours: isDismissed ? {
            monday: 0,
            tuesday: 0,
            wednesday: 0,
            thursday: 0,
            friday: 0,
            saturday: 0,
            sunday: 0,
          } : booking.scheduledHours,
          description: isDismissed ? '' : 'Work as scheduled',
          isFromSchedule: true,
          scheduledBookingId: booking.id,
          scheduledHours: booking.scheduledHours,
          bookingCreatedBy: booking.createdBy,
          bookingCreatedByName: booking.createdByName,
          bookingNotes: booking.notes,
          bookingCreatedAt: booking.createdAt,
          isDismissed,
          dismissalReason: isDismissed ? dismissalReason : undefined,
          dismissedAt: isDismissed ? addDays(timesheet.weekEnding, -3) : undefined,
        });
      }
    });

    // Add remaining entries that weren't scheduled bookings
    timesheet.entries.forEach(entry => {
      const key = `${entry.projectId}-${entry.costCentreId}`;
      if (!processedKeys.has(key)) {
        enhancedEntries.push(entry);
      }
    });

    return {
      ...timesheet,
      entries: enhancedEntries,
    };
  }).map((timesheet, index, array) => {
    // Log summary after processing all timesheets
    if (index === array.length - 1) {
      console.log('='.repeat(80));
      console.log(`🎉 ENHANCEMENT COMPLETE - Enhanced ${enhancedCount} timesheets with scheduled bookings`);
      console.log('='.repeat(80));
      
      // Store in window for debugging
      (window as any).__ENHANCED_COUNT__ = enhancedCount;
    }
    return timesheet;
  });
};

// Convert ProjectDetails from projectsData.ts to Project format for timesheet
const convertProjectDetailsToProject = (): Project[] => {
  return projectsFromProjectsData.map(pd => ({
    id: pd.id,
    code: pd.code,
    name: pd.name,
    clientId: pd.clientId,
    clientName: pd.clientName,
    costCentres: pd.timeEntryCodes?.map(tec => ({
      id: tec.id,
      code: tec.code,
      name: tec.name
    })) || costCentres,
    billable: pd.billable,
    active: pd.status === 'active',
    teamSize: pd.teamSize
  }));
};

// Initialize all mock data
// Add specific test absence entries
const addTestAbsenceEntries = (timesheets: Timesheet[]): Timesheet[] => {
  const currentWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
  const currentWeekEnding = addDays(currentWeek, 6);
  
  return timesheets.map(timesheet => {
    // Add absence entry for James Smith (employee ID 1) for CURRENT WEEK (Draft status)
    if (timesheet.employeeId === 1 && timesheet.weekEnding.getTime() === currentWeekEnding.getTime()) {
      const absenceEntry: TimesheetEntry = {
        id: -9999, // Unique negative ID for test entry
        projectId: 'ABSENCE',
        costCentreId: 'N/A',
        description: 'Annual Leave',
        hours: {
          monday: 7.5,
          tuesday: 7.5,
          wednesday: 7.5,
          thursday: 0,
          friday: 0,
          saturday: 0,
          sunday: 0
        },
        isReadOnly: true,
        isNonProjectTime: true,
        absenceType: 'annual-leave',
        sourceIntegration: 'people-hr',
        projectCode: 'ABSENCE',
        projectName: 'Annual Leave',
        absenceId: `ABS-${new Date().getFullYear()}-001234`,
        absenceReason: 'Family holiday',
        approvedBy: 'Sarah Jones',
        approvedAt: addDays(currentWeekEnding, -7) // Approved a week before
      };
      
      return {
        ...timesheet,
        entries: [absenceEntry, ...timesheet.entries]
      };
    }
    
    return timesheet;
  });
};

export const initializeMockData = () => {
  const clients = generateClients();
  // Use centralized project data from projectsData.ts instead of generating new ones
  const projects = convertProjectDetailsToProject();
  const employees = generateEmployees();
  let timesheets = generateTimesheets(employees, projects);
  
  // Enhance timesheets with scheduled booking data
  timesheets = enhanceTimesheetsWithScheduledBookings(timesheets, projects);
  
  // Add test absence entries
  timesheets = addTestAbsenceEntries(timesheets);

  return {
    clients,
    projects,
    employees,
    timesheets
  };
};
